import React, {useEffect, useRef, useState} from 'react';
import {useTranslate} from 'react-admin';
import {Box, Dialog, Typography} from '@mui/material';
import {t} from '../themes/statamicTokens';
import {axiosApiInstance} from '../AxiosUtils';
import PrimaryButton from '../components/statamic/PrimaryButton';
import EmptyState from '../components/statamic/EmptyState';
import CommentItem from './CommentItem';
import CommentInput from './CommentInput';

export const loadCommentsForPost = (postId) =>
    axiosApiInstance.get(`${process.env.REACT_APP_API_URL}/api/v1/updatepost/${postId}/comments`);

export const deleteComment = (postId, commentId) =>
    axiosApiInstance.delete(`${process.env.REACT_APP_API_URL}/api/v1/updatepost/${postId}/comment/${commentId}`);

export const postComment = (postId, message, commentId = 0) => {
    const formData = new FormData();
    formData.append('message', message);
    let url = `${process.env.REACT_APP_API_URL}/api/v1/updatepost/${postId}/comment`;
    if (commentId > 0) url += `/${commentId}`;
    return axiosApiInstance.post(url, formData, {
        headers: {'Content-Type': 'multipart/form-data'},
    });
};

export const likeComment = (postId, commentId, liked) => {
    const formData = new FormData();
    formData.append('liked', liked);
    return axiosApiInstance.post(
        `${process.env.REACT_APP_API_URL}/api/v1/updatepost/${postId}/comment/${commentId}/liked`,
        formData,
        {headers: {'Content-Type': 'multipart/form-data'}},
    );
};

const PostComments = ({postId}) => {
    const translate = useTranslate();

    const [comments, setComments] = useState([]);
    const [replyCommentId, setReplyCommentId] = useState(0);
    const [replyingToMessage, setReplyingToMessage] = useState('');
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState(0);

    const commentInputRef = useRef();

    const loadComments = (id) => {
        loadCommentsForPost(id)
            .then((res) => {
                if (res.status === 200 && res.data) setComments(res.data.comments ?? []);
            })
            .catch(() => {});
    };

    useEffect(() => { loadComments(postId); }, [postId]);

    const handleToggleLike = (comment) => {
        if (!comment?.id) return;
        likeComment(postId, comment.id, !comment.ilike).then(() => loadComments(postId)).catch(() => {});
    };

    const handlePost = (message, commentId = 0) => {
        postComment(postId, message, commentId).then(() => {
            loadComments(postId);
            setReplyCommentId(0);
            setReplyingToMessage('');
        }).catch(() => {});
    };

    const handleReply = (commentId, message) => {
        commentInputRef.current?.focus();
        setReplyingToMessage(message);
        setReplyCommentId(commentId);
    };

    const handleClickDelete = (id) => {
        setPendingDeleteId(id);
        setDeleteOpen(true);
    };

    const confirmDelete = () => {
        deleteComment(postId, pendingDeleteId).then((res) => {
            if (res.status === 200) {
                loadComments(postId);
                setDeleteOpen(false);
            }
        }).catch(() => setDeleteOpen(false));
    };

    const hasComments = comments && comments.length > 0;

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
            {!hasComments && (
                <EmptyState
                    title={translate('No comments yet')}
                    description={translate('Be the first to leave a comment on this update.')}
                    dense
                />
            )}
            {hasComments && comments.map((comment) => (
                <Box key={comment.id}>
                    <CommentItem
                        comment={comment}
                        onReply={handleReply}
                        onToggleLikeComment={handleToggleLike}
                        onDelete={handleClickDelete}
                    />
                    {comment.reactions?.map((reaction) => (
                        <CommentItem
                            key={reaction.id}
                            comment={reaction}
                            onToggleLikeComment={handleToggleLike}
                            onDelete={handleClickDelete}
                            isReaction
                        />
                    ))}
                </Box>
            ))}

            {replyingToMessage && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1,
                        py: 0.75,
                        borderRadius: `${t.radius.sm}px`,
                        backgroundColor: t.color.accent.primaryTint,
                        border: `1px solid ${t.color.border.default}`,
                        fontSize: `${t.type.caption.size}px`,
                        color: t.color.text.secondary,
                    }}
                >
                    <Box component="span" sx={{flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                        {translate('Replying to')}: <em>{replyingToMessage}</em>
                    </Box>
                    <PrimaryButton
                        variant="ghost"
                        onClick={() => { setReplyCommentId(0); setReplyingToMessage(''); }}
                        sx={{minHeight: 24, px: 1, fontSize: `${t.type.caption.size}px`}}
                    >
                        {translate('Reply to post')}
                    </PrimaryButton>
                </Box>
            )}

            <CommentInput
                onSubmit={handlePost}
                commentInputRef={commentInputRef}
                commentId={replyCommentId}
            />

            <Dialog
                open={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: `${t.radius.lg}px`,
                            border: `1px solid ${t.color.border.default}`,
                            boxShadow: t.shadow.modal,
                            maxWidth: 440,
                        },
                    },
                }}
            >
                <Box sx={{p: 3}}>
                    <Typography
                        sx={{fontSize: `${t.type.h3.size}px`, fontWeight: t.type.h3.weight, color: t.color.text.primary}}
                    >
                        {translate('Delete comment alert title')}
                    </Typography>
                    <Typography sx={{mt: 1, fontSize: `${t.type.small.size}px`, color: t.color.text.secondary, lineHeight: 1.5}}>
                        {translate('Delete comment alert message')}
                    </Typography>
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3}}>
                        <PrimaryButton variant="secondary" onClick={() => setDeleteOpen(false)}>
                            {translate('No, cancel')}
                        </PrimaryButton>
                        <PrimaryButton variant="danger" onClick={confirmDelete}>
                            {translate('Yes, delete')}
                        </PrimaryButton>
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
};

export default PostComments;
